import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { forexApi, type ConfigPackage } from "@/lib/forex-api";

function formatTime(timestamp?: number | null) {
  if (!timestamp) return "未发布";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 16);
}

export default function ConfigCenterTab() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<ConfigPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    setLoading(true); setError("");
    forexApi
      .listConfigPackages()
      .then((result) => setConfigs(result.items))
      .catch(() => setError("配置中心加载失败"))
      .finally(() => setLoading(false));
  }, [revision]);

  const openDetail = (key: string) => {
    if (key === "symbol_config") return navigate("/forex/symbol-config/detail");
    if (key === "risk_config") return navigate("/forex/risk-config/detail");
    if (key === "query_policy") return navigate("/forex/query-policy/detail");
    return navigate(`/forex/config/detail?type=${key}`);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">配置中心</h2>
        <p className="text-xs text-text-muted mt-0.5">
          查看配置包的业务用途、版本和发布记录。后台发布记录与 C 端实际读取结果应分别核对；此处不能证明配置已生效。目标接口：{" "}
          <code className="text-brand bg-brand/5 px-2 py-0.5 rounded text-xs font-mono">
            GET /app/config/bootstrap
          </code>{" "}
          。点击卡片进入详情。
        </p>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-text-muted">{loading ? "加载中..." : `共 ${configs.length} 个配置包`}</span>
        <span className="text-[10px] text-text-muted bg-surface border border-surface-border rounded px-2 py-1">
          /quantum/business/forex/config-packages
        </span>
      </div>

      {error && <div role="alert" className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}，当前配置状态未知。<button onClick={() => setRevision(value => value + 1)} className="ml-2 underline">重试</button></div>}
      {!loading && !error && !configs.length && <div className="rounded-xl border bg-white p-8 text-center text-sm text-text-muted">暂无配置包记录。请确认所选环境及配置来源；此处不代表 C 端已启用配置。</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {configs.map((cfg) => (
          <button
            key={cfg.config_key}
            onClick={() => openDetail(cfg.config_key)}
            className="bg-white rounded-xl border border-surface-border p-4 hover:border-brand/30 hover:shadow-sm transition-all text-left cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-text-primary">{cfg.label}</span>
                <span className="text-[10px] text-text-muted font-mono bg-surface px-1.5 py-0.5 rounded">{cfg.config_key}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${cfg.status === "enabled" ? "bg-emerald-500/10 text-emerald-500" : "bg-text-muted/10 text-text-muted"}`}>
                  {cfg.status === "enabled" ? "已启用" : "已停用"}
                </span>
              </div>
              <i className="ri-arrow-right-s-line text-text-muted text-lg" />
            </div>
            <p className="text-xs text-text-muted mb-3">{cfg.description}</p>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="inline-flex items-center gap-1 text-emerald-500 bg-emerald-500/5 px-1.5 py-0.5 rounded border border-emerald-500/15">
                <i className="ri-git-branch-line" />{cfg.version}
              </span>
              <span className="text-text-muted">{formatTime(cfg.last_published_at)}</span>
              <code className="text-text-muted font-mono truncate ml-auto max-w-[220px]">{`${cfg.api_path} -> ${cfg.config_key}`}</code>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
